import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@truepill/react-capsule'
import { LaunchDarkly, OrderStatus, OtcProductStatus, UserRoles } from '@truepill/tpos-types'
import { ReactComponent as CheckboxIcon } from 'assets/icons/checkmark.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as FlagIcon } from 'assets/icons/flag.svg'
import IconWrapper from 'components/IconWrapper'
import CancelOtcButton from 'components/OrderView/components/CancelOtcButton'
import UncancelOtcButton from 'components/OrderView/components/UncancelOtcButton'
import { StyledTextInput as TextInput } from 'components/RXTable'
import { UPDATE_OTC_SERIAL_NUMBERS } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { useClient, useFlag } from 'providers/LaunchDarklyProvider'
import type { PackingAdditonalData } from 'providers/TaskProvider'
import { useTaskContext, type Task } from 'providers/TaskProvider'
import { usePlusClient } from 'providers/VisionRouter'
import styled, { css } from 'styled-components'
import EllipsisTruncate from 'styles/EllipsisTruncate'
import { darkInfo, primaryBackgroundColor } from 'styles/styleVariables'
import type { Customer, RejectionDetails, TaskProductType, TPOSLocation } from 'types'
import { getScannedSerialNumbers, getScannedUpcs, trimCommas, trimLeadingCommas } from 'utils'
import { isSplitCancelled } from 'utils/splitOrder'
import { shouldCompleteTask } from 'utils/tasksUtils'

const getCompletedTasks = (tasks: Task[]) => {
  if (tasks.length > 0) {
    return tasks.filter(task => task.completed).length
  }

  return 0
}

const getScansInfo = (tasks: Task[]) => {
  const complete = getCompletedTasks(tasks)
  return { complete, isCompleted: tasks.length - complete === 0 }
}

interface ScannableProduct {
  _id: string
  name?: string
  overrideScan?: boolean
  sku: string
  quantity: number
  isSerialized?: boolean
  serialNumbers?: string[]
  otcStatus?: string
  rejectionDetails?: RejectionDetails
}

const AccuracyScanningItem = (props: {
  orderId: string
  product: ScannableProduct
  tasks: Task[]
  packingData: { error?: string; packSize: number }
  productType: TaskProductType
  handleOverride?: () => void
  scanningDisabled?: boolean
  customer?: Customer
  location?: TPOSLocation
  orderStatus?: OrderStatus
}): JSX.Element => {
  const showCancelOtcComponentsFlag = useFlag(LaunchDarkly.FeatureFlags.TEMP_CANCEL_OTC_BUTTON)
  const {
    orderId,
    product,
    tasks,
    packingData,
    handleOverride,
    productType,
    scanningDisabled,
    customer,
    location,
    orderStatus,
  } = props
  const { tokenContext } = usePlusClient()
  const { hasRole, isAdmin } = tokenContext
  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast(true)
  const { updateTask, completeTask } = useTaskContext()
  const itemTasks = tasks.filter(task => task.productType === productType && task.key.includes(product._id))
  const hasItemTasks = itemTasks.length > 0
  const { complete, isCompleted } = getScansInfo(itemTasks)
  const [overriden, setIsOverridden] = useState(product.overrideScan)

  const completed = overriden || (isCompleted && !packingData.error)
  const cancel = overriden ? false : !hasItemTasks || !!packingData.error
  const isSerialized = product.isSerialized || false
  const otcProductIsCancelled = product.otcStatus === OtcProductStatus.Cancelled

  const [updateOtcSerialNumbers] = useMutation(UPDATE_OTC_SERIAL_NUMBERS, {
    onError: (e: Error) => showErrorToast('Failed to save serial numbers to OTC Product:' + e.message),
    onCompleted: () => showSuccessToast('Serial numbers saved to OTC Product'),
  })

  const [manualSerialNumbers, setManualSerialNumbers] = useState<string>(product.serialNumbers?.join(',') || '')
  const scannedSerialNumbers: string[] = getScannedSerialNumbers(itemTasks)
  const serialNumberInput = [...scannedSerialNumbers, manualSerialNumbers].filter(sn => sn.trim() !== '').join(',')
  const showCancelOtcComponents =
    showCancelOtcComponentsFlag && productType === 'OTC' && orderStatus !== OrderStatus.Complete

  // Set the customer, location and ndc for feature-flag check
  const { ldReady, ldIdentify } = useClient()

  useEffect(() => {
    if (ldReady && customer?.legacyId && location?.legacyId) {
      ldIdentify({
        key: customer?.legacyId.toString(),
        custom: {
          location: location?.legacyId.toString(),
        },
      })
    }
  }, [ldReady, ldIdentify, customer, location])

  const overrideScan = () => {
    if (
      isAdmin() ||
      hasRole([
        UserRoles.Pharmacist,
        UserRoles.LeadPharmacist,
        UserRoles.LeadWarehouse,
        UserRoles.LeadCustomerSupport,
        UserRoles.LeadOtcOnly,
      ])
    ) {
      if (isSerialized) {
        const scannedCount = scannedSerialNumbers.length
        const manualSerialNumbersArray = trimCommas(manualSerialNumbers).split(',')
        const manualCount = manualSerialNumbersArray.length
        if (manualCount + scannedCount < itemTasks.length) {
          showErrorToast('Serial Number Entry is Required')
          return
        }
        const otcSerialNumbers = [...scannedSerialNumbers, ...manualSerialNumbersArray]

        const duplicatedSerialNumbers = otcSerialNumbers.filter(
          (item, index) => otcSerialNumbers.indexOf(item) !== index,
        )
        if (duplicatedSerialNumbers.length > 0) {
          showErrorToast(
            `Duplicated serial numbers found: ${[...new Set(duplicatedSerialNumbers)].join(
              ', ',
            )}. Please scan the correct items.`,
          )
          return
        }

        updateOtcSerialNumbers({
          variables: { orderId, otcProductId: product._id, serialNumbers: otcSerialNumbers },
        })
      }

      setIsOverridden(true)
      itemTasks.forEach(task => {
        updateTask(task.key, {
          overrideScan: true,
          completed: true,
        })
      })

      handleOverride?.()
      showSuccessToast(`${productType === 'OTC' ? 'OTC' : 'Packing Material'} overridden`)
    } else {
      showErrorToast('Only leads can override scanning')
    }
  }

  const handleSerialNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // only allow manual input if not all serial numbers have been scanned
    if (scannedSerialNumbers.length < itemTasks.length) {
      // Filter out scanned sns and non alphanumeric or comma characters
      let manualSerialInput = e.target.value
        .substring(scannedSerialNumbers.join('').length + (scannedSerialNumbers.length - 1))
        .replace(/[^a-zA-Z0-9,]/g, '')

      // if this is the last serial number being input
      if (
        scannedSerialNumbers.length === itemTasks.length - 1 ||
        serialNumberInput.split(',').length === itemTasks.length
      ) {
        // don't allow input if the last character is a comma
        if (manualSerialInput !== ',' && manualSerialInput[manualSerialInput.length - 1] === ',') {
          return
        }
      } else if (manualSerialInput.slice(-2) === ',,') {
        return
      }

      manualSerialInput = trimLeadingCommas(manualSerialInput)
      setManualSerialNumbers(manualSerialInput)
    }
  }

  const handleSerialNumberBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmedManualSerialNumbers = trimCommas(manualSerialNumbers)
    setManualSerialNumbers(trimmedManualSerialNumbers)
    const manualSerialNumbersArray = trimmedManualSerialNumbers.split(',')
    const unscannedSNTasks = itemTasks.filter(task => !task.additionalData?.scannedSerialNumber)

    // update tasks with manual entry serial numbers
    manualSerialNumbersArray.forEach((sn, index) => {
      if (unscannedSNTasks[index]) {
        const updatedAdditionalData: PackingAdditonalData = {
          ...unscannedSNTasks[index].additionalData,
          upcOptions: unscannedSNTasks[index].additionalData?.upcOptions || [],
          manualSerialNumber: sn,
        }
        updateTask(unscannedSNTasks[index].key, {
          additionalData: updatedAdditionalData,
        })
        if (shouldCompleteTask(isSerialized, updatedAdditionalData)) {
          completeTask(unscannedSNTasks[index].key)
        }
      }
    })
  }

  return (
    <StyledItem data-testid="AccuracyScanningItem" data-sku={product.sku} cancelled={otcProductIsCancelled}>
      <ItemHeader>
        <Line>
          <CheckMarkIcon completed={completed} cancel={cancel} error={!!packingData.error && !overriden} />
          <HeaderLabel>Name:</HeaderLabel>
          <HeaderText data-testid="sku">{product.name || 'N/A'}</HeaderText>

          {showCancelOtcComponents && product.otcStatus !== OtcProductStatus.Cancelled && (
            <CancelOtcButton orderId={orderId} otcProductId={product._id} sku={product.sku} />
          )}
          {showCancelOtcComponents &&
            product.otcStatus === OtcProductStatus.Cancelled &&
            !isSplitCancelled(product.rejectionDetails) && (
              <UncancelOtcButton orderId={orderId} otcProductId={product._id} />
            )}
        </Line>
      </ItemHeader>
      <ItemBody>
        <Info>
          {!scanningDisabled && !otcProductIsCancelled && (
            <Line>
              <ScanningFieldLabel>UPC:</ScanningFieldLabel>
              <ScanningFieldText>
                <StyledTextInput
                  value={cancel ? '' : getScannedUpcs(itemTasks)}
                  placeholder={overriden || cancel ? 'Manual check required' : 'Scan UPC'}
                  readOnly={true}
                />
              </ScanningFieldText>
            </Line>
          )}

          {isSerialized && !otcProductIsCancelled && (
            <Line>
              <ScanningFieldLabel>SN:</ScanningFieldLabel>
              <ScanningFieldText>
                <StyledTextInput
                  value={cancel ? '' : serialNumberInput}
                  placeholder={overriden || cancel ? 'Manual check required' : 'Scan or enter serial number'}
                  readOnly={cancel || overriden}
                  onBlur={handleSerialNumberBlur}
                  onChange={handleSerialNumberChange}
                />
              </ScanningFieldText>
            </Line>
          )}

          <Line>
            <ContentLabel>SKU:</ContentLabel>
            <Text data-testid="sku">{product.sku}</Text>
          </Line>
          {packingData.packSize ? (
            <Line>
              <ContentLabel>Pack size:</ContentLabel>
              <Text data-testid="packSize">{packingData.packSize}</Text>
            </Line>
          ) : (
            <></>
          )}
          <Line>
            <ContentLabel>Quantity:</ContentLabel>
            <Text data-testid="quantity">
              {product.quantity}
              {packingData.error && ` (${packingData.error})`}
              {hasItemTasks && ` (Pack ${complete} of ${itemTasks.length})`}
            </Text>
          </Line>
          {!scanningDisabled && !completed && !otcProductIsCancelled && (!cancel || !!packingData.error) ? (
            <LineOverride>
              <OverrideButton variant="primary-text" onClick={overrideScan}>
                Override
              </OverrideButton>
            </LineOverride>
          ) : (
            <LineEmptyOverride></LineEmptyOverride>
          )}
        </Info>
      </ItemBody>
    </StyledItem>
  )
}

const CheckMarkIcon = (props: { completed?: boolean; cancel?: boolean; error?: boolean }): JSX.Element => {
  const { completed, cancel, error } = props
  let icon: React.ReactElement | undefined

  if (error) {
    icon = <FlagIcon fill="white" />
  } else {
    icon = cancel ? <CloseIcon fill="white" /> : completed ? <CheckboxIcon /> : undefined
  }

  return (
    <CheckMarkIconWrapper>
      <StyledTaskBadge completed={completed} cancel={cancel} error={error}>
        {icon ? <IconWrapper>{icon}</IconWrapper> : <></>}
      </StyledTaskBadge>
    </CheckMarkIconWrapper>
  )
}

export default AccuracyScanningItem

const OverrideButton = styled(Button)`
  width: auto;
  padding: 0;
  font-size: 12px;
  color: ${darkInfo};
  text-decoration: underline;
`

const LineOverride = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3rem;
`
const LineEmptyOverride = styled.div`
  display: block;
  padding: 1.5rem;
`

const StyledTextInput = styled(TextInput)`
  padding: 0.2rem 0.4rem;
  font-size: 12px;
  border-radius: 8px;
  min-width: 10rem;
  width: 100%;
`

const CheckMarkIconWrapper = styled.div`
  display: inline-flex;
`

const StyledItem = styled.li<{ cancelled?: boolean }>`
  margin: 4px;
  padding: 0.5rem 1rem;
  background-color: ${primaryBackgroundColor};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex-basis: calc(33.333% - 0.5rem);
  ${({ cancelled }) => cancelled && 'opacity: 0.5;'}
`

const ItemHeader = styled.div`
  padding-bottom: 0.2rem;
`

const ItemBody = styled.div``

const Line = styled.div`
  display: flex;
  margin-top: 0.2rem;
  width: 100%;
`

const ContentLabel = styled.span`
  display: inline-block;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  padding-left: 0.5rem;
`

const Text = styled.span`
  display: inline-block;
  font-family: 'Lato', sans-serif;
  font-weight: 400;
  margin-left: 0.2rem;
  font-size: 12px;
`

const HeaderLabel = styled.span`
  display: inline-flex;
  height: 1.75rem;
  padding-left: 0.5rem;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 14px;
  padding-top: 0.1rem;
`

const HeaderText = styled(Text)`
  font-size: 14px;
  width: 70%;
  display: inline-flex;
  padding-top: 0.1rem;
`

const ScanningFieldLabel = styled(ContentLabel)`
  margin-top: 0.2rem;
  width: 100%;
  max-width: 2.5rem;
`

const ScanningFieldText = styled(Text)`
  font-size: 14px;
  height: 1.75rem;
  width: 80%;
  margin-bottom: 0.5rem;
`

const Info = styled.div`
  display: block;
  font-size: 1rem;
  > span {
    ${EllipsisTruncate}
  }
`

const FilledBadge = css`
  background-color: #034d83;
`

const CompletedBadgeBorder = css`
  border: 0.125rem solid #034d83;
`

const BadgeBorder = css`
  border: 0.125rem solid #034d83;
`

const FilledBadgeCancel = css`
  background-color: #9eacbc;
`

const CompletedBadgeBorderCancel = css`
  border: 0.125rem solid #9eacbc;
`

const StyledTaskBadge = styled.div<{ completed?: boolean; cancel?: boolean; error?: boolean }>`
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${primaryBackgroundColor};
  ${({ completed }) => completed && FilledBadge}
  ${({ completed }) => completed && CompletedBadgeBorder}
  ${({ completed }) => !completed && BadgeBorder}
  ${({ cancel }) => cancel && FilledBadgeCancel}
  ${({ cancel }) => cancel && CompletedBadgeBorderCancel}
  ${({ error }) => error && 'background-color: #af5304;'}
  ${({ error }) => error && 'border-color: #af5304;'}
`
